import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { PageLoader, TableSkeleton } from '../components/Loading';
import { useAuth } from '../context/AuthContext';
import {
  formatCurrency,
  formatDate,
  getAgentStatusBadgeClass,
  AGENT_STATUSES,
} from '../lib/utils';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import { Plus, Pencil, Trash2, Users, X, Phone, Mail, Calendar } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AgentsPage() {
  const { isMD } = useAuth();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentDetail, setAgentDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    email: '',
    status: 'Active',
    commission_rate: 5.0,
    password: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      const response = await axios.get(`${API}/agents`);
      setAgents(response.data);
    } catch (error) {
      console.error('Error fetching agents:', error);
      toast.error('Failed to load agents');
    } finally {
      setLoading(false);
    }
  };

  const fetchAgentDetail = async (agentId) => {
    setLoadingDetail(true);
    try {
      const response = await axios.get(`${API}/agents/${agentId}`);
      setAgentDetail(response.data);
    } catch (error) {
      console.error('Error fetching agent detail:', error);
      toast.error('Failed to load agent details');
    } finally {
      setLoadingDetail(false);
    }
  };

  const resetForm = () => {
    setFormData({
      full_name: '',
      phone: '',
      email: '',
      status: 'Active',
      commission_rate: 5.0,
      password: '',
    });
    setFormErrors({});
    setSelectedAgent(null);
  };

  const openAddModal = () => {
    resetForm();
    setModalOpen(true);
  };

  const openEditModal = (agent, e) => {
    e?.stopPropagation();
    setSelectedAgent(agent);
    setFormData({
      full_name: agent.full_name,
      phone: agent.phone,
      email: agent.email,
      status: agent.status,
      commission_rate: agent.commission_rate,
      password: '',
    });
    setFormErrors({});
    setModalOpen(true);
  };

  const openDeleteDialog = (agent, e) => {
    e?.stopPropagation();
    setSelectedAgent(agent);
    setDeleteDialogOpen(true);
  };

  const openDetailPanel = (agent) => {
    setSelectedAgent(agent);
    fetchAgentDetail(agent.id);
    setDetailOpen(true);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.full_name.trim()) errors.full_name = 'Name is required';
    if (!formData.phone.trim()) errors.phone = 'Phone is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    if (!formData.commission_rate || formData.commission_rate < 0) errors.commission_rate = 'Valid rate is required';
    if (!selectedAgent && !formData.password) errors.password = 'Password is required for new agents';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const payload = {
        full_name: formData.full_name,
        phone: formData.phone,
        email: formData.email,
        status: formData.status,
        commission_rate: parseFloat(formData.commission_rate),
      };

      if (selectedAgent) {
        await axios.put(`${API}/agents/${selectedAgent.id}`, payload);
        toast.success('Agent updated successfully');
      } else {
        payload.password = formData.password;
        await axios.post(`${API}/agents`, payload);
        toast.success('Agent created successfully');
      }

      setModalOpen(false);
      resetForm();
      fetchAgents();
    } catch (error) {
      console.error('Error saving agent:', error);
      toast.error(error.response?.data?.detail || 'Failed to save agent');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/agents/${selectedAgent.id}`);
      toast.success('Agent deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedAgent(null);
      fetchAgents();
    } catch (error) {
      console.error('Error deleting agent:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete agent');
    }
  };

  return (
    <Layout>
      <div className="space-y-6 fade-in" data-testid="agents-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-4xl text-lux-text mb-2">Agents</h1>
            <p className="text-lux-muted">Manage your sales team</p>
          </div>
          {isMD && (
            <Button onClick={openAddModal} className="lux-btn-primary" data-testid="add-agent-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Agent
            </Button>
          )}
        </div>

        {/* Table */}
        <div className="lux-card overflow-hidden">
          {loading ? (
            <TableSkeleton rows={5} cols={8} />
          ) : agents.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Users className="w-16 h-16 text-lux-border mb-4" />
              <p className="text-lux-muted text-lg">No agents found</p>
              {isMD && (
                <Button onClick={openAddModal} className="lux-btn-secondary mt-4">
                  Add your first agent
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="lux-table-header">
                  <tr>
                    <th className="px-6 py-4 text-left">Code</th>
                    <th className="px-6 py-4 text-left">Name</th>
                    <th className="px-6 py-4 text-left">Phone</th>
                    <th className="px-6 py-4 text-left">Email</th>
                    <th className="px-6 py-4 text-left">Status</th>
                    {isMD && <th className="px-6 py-4 text-right">Commission %</th>}
                    <th className="px-6 py-4 text-center">Deals</th>
                    {isMD && <th className="px-6 py-4 text-right">Total Sales</th>}
                    {isMD && <th className="px-6 py-4 text-right">Earned (₦)</th>}
                    {isMD && <th className="px-6 py-4 text-right">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {agents.map((agent) => (
                    <tr
                      key={agent.id}
                      className="lux-table-row cursor-pointer"
                      onClick={() => openDetailPanel(agent)}
                      data-testid={`agent-row-${agent.id}`}
                    >
                      <td className="px-6 py-4 text-lux-gold font-mono text-sm">
                        {agent.agent_code}
                      </td>
                      <td className="px-6 py-4 text-lux-text font-medium">
                        {agent.full_name}
                      </td>
                      <td className="px-6 py-4 text-lux-muted">{agent.phone}</td>
                      <td className="px-6 py-4 text-lux-muted">{agent.email}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getAgentStatusBadgeClass(agent.status)}`}>
                          {agent.status}
                        </span>
                      </td>
                      {isMD && (
                        <td className="px-6 py-4 text-right text-lux-text">
                          {agent.commission_rate}%
                        </td>
                      )}
                      <td className="px-6 py-4 text-center text-lux-gold font-semibold">
                        {agent.deal_count || 0}
                      </td>
                      {isMD && (
                        <td className="px-6 py-4 text-right text-lux-gold">
                          {formatCurrency(agent.total_sales || 0)}
                        </td>
                      )}
                      {isMD && (
                        <td className="px-6 py-4 text-right text-green-400 font-semibold">
                          {formatCurrency(agent.total_earned || 0)}
                        </td>
                      )}
                      {isMD && (
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => openEditModal(agent, e)}
                              className="text-lux-muted hover:text-lux-gold"
                              data-testid={`edit-agent-${agent.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => openDeleteDialog(agent, e)}
                              className="text-lux-muted hover:text-red-400"
                              data-testid={`delete-agent-${agent.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Agent Detail Side Panel */}
        <Sheet open={detailOpen} onOpenChange={setDetailOpen}>
          <SheetContent className="bg-lux-card border-lux-border w-full sm:max-w-lg overflow-y-auto">
            <SheetHeader>
              <SheetTitle className="font-heading text-2xl text-lux-text">
                Agent Details
              </SheetTitle>
            </SheetHeader>
            {loadingDetail ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-2 border-lux-border border-t-lux-gold rounded-full animate-spin" />
              </div>
            ) : agentDetail ? (
              <div className="mt-6 space-y-6">
                {/* Agent Info */}
                <div className="p-4 bg-lux-row rounded-lg">
                  <h3 className="font-heading text-xl text-lux-gold mb-3">
                    {agentDetail.full_name}
                  </h3>
                  <p className="text-lux-muted text-sm font-mono mb-3">{agentDetail.agent_code}</p>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-lux-muted">
                      <Phone className="w-4 h-4" />
                      {agentDetail.phone}
                    </div>
                    <div className="flex items-center gap-2 text-lux-muted">
                      <Mail className="w-4 h-4" />
                      {agentDetail.email}
                    </div>
                    <div className="flex items-center gap-2 text-lux-muted">
                      <Calendar className="w-4 h-4" />
                      Joined {formatDate(agentDetail.date_joined)}
                    </div>
                  </div>
                </div>

                {/* Stats - Only MD sees commission rate */}
                <div className="grid grid-cols-2 gap-4">
                  {isMD && (
                    <div className="p-4 bg-lux-row rounded-lg text-center">
                      <p className="text-lux-muted text-sm">Commission Rate</p>
                      <p className="font-heading text-2xl text-lux-gold">{agentDetail.commission_rate}%</p>
                    </div>
                  )}
                  <div className={`p-4 bg-lux-row rounded-lg text-center ${!isMD ? 'col-span-2' : ''}`}>
                    <p className="text-lux-muted text-sm">Status</p>
                    <p className={`font-heading text-2xl ${agentDetail.status === 'Active' ? 'text-green-400' : 'text-red-400'}`}>
                      {agentDetail.status}
                    </p>
                  </div>
                </div>

                {/* Sales - Only MD sees detailed sales info */}
                {isMD ? (
                  <>
                    <div>
                      <h4 className="font-heading text-lg text-lux-text mb-3">Individual Sales</h4>
                      {agentDetail.sales?.length > 0 ? (
                        <div className="space-y-2">
                          {agentDetail.sales.map((sale) => (
                            <div key={sale.id} className="p-3 bg-lux-row rounded-lg">
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className="text-lux-text font-medium text-sm">{sale.property?.name}</p>
                                  <p className="text-lux-muted text-xs">{sale.sale_code} • {formatDate(sale.sale_date)}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-lux-gold text-sm">{formatCurrency(sale.sale_amount)}</p>
                                  <p className="text-green-400 text-xs">+{formatCurrency(sale.commission_earned)}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-lux-muted text-sm py-4 text-center">No individual sales yet</p>
                      )}
                    </div>

                    {/* Collaborative Sales */}
                    {agentDetail.collaborative_sales?.length > 0 && (
                      <div>
                        <h4 className="font-heading text-lg text-lux-text mb-3">Collaborative Sales</h4>
                        <div className="space-y-2">
                          {agentDetail.collaborative_sales.map((sale) => (
                            <div key={sale.id} className="p-3 bg-lux-row rounded-lg border-l-2 border-lux-gold">
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className="text-lux-text font-medium text-sm">{sale.property?.name}</p>
                                  <p className="text-lux-muted text-xs">{sale.sale_code} • {formatDate(sale.sale_date)}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-lux-gold text-sm">{formatCurrency(sale.sale_amount)}</p>
                                  <p className="text-green-400 text-xs">+{formatCurrency(sale.commission_earned)}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="p-4 bg-lux-row rounded-lg text-center">
                    <p className="text-lux-muted text-sm">Total Deals Closed</p>
                    <p className="font-heading text-3xl text-lux-gold">{(agentDetail.sales?.length || 0) + (agentDetail.collaborative_sales?.length || 0)}</p>
                  </div>
                )}
              </div>
            ) : null}
          </SheetContent>
        </Sheet>

        {/* Add/Edit Modal */}
        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogContent className="bg-lux-card border-lux-border max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl text-lux-text">
                {selectedAgent ? 'Edit Agent' : 'Add New Agent'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-lux-text">Full Name *</Label>
                <Input
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className={`lux-input ${formErrors.full_name ? 'border-red-500' : ''}`}
                  placeholder="Agent name"
                  data-testid="agent-name-input"
                />
                {formErrors.full_name && <p className="text-red-400 text-xs">{formErrors.full_name}</p>}
              </div>
              <div className="space-y-2">
                <Label className="text-lux-text">Phone *</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className={`lux-input ${formErrors.phone ? 'border-red-500' : ''}`}
                  placeholder="+234 xxx xxx xxxx"
                  data-testid="agent-phone-input"
                />
                {formErrors.phone && <p className="text-red-400 text-xs">{formErrors.phone}</p>}
              </div>
              <div className="space-y-2">
                <Label className="text-lux-text">Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={`lux-input ${formErrors.email ? 'border-red-500' : ''}`}
                  placeholder="agent@example.com"
                  data-testid="agent-email-input"
                />
                {formErrors.email && <p className="text-red-400 text-xs">{formErrors.email}</p>}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-lux-text">Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData({ ...formData, status: value })}
                  >
                    <SelectTrigger className="lux-input" data-testid="agent-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {AGENT_STATUSES.map((status) => (
                        <SelectItem key={status} value={status}>{status}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-lux-text">Commission Rate % *</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.commission_rate}
                    onChange={(e) => setFormData({ ...formData, commission_rate: e.target.value })}
                    className={`lux-input ${formErrors.commission_rate ? 'border-red-500' : ''}`}
                    data-testid="agent-commission-input"
                  />
                  {formErrors.commission_rate && <p className="text-red-400 text-xs">{formErrors.commission_rate}</p>}
                </div>
              </div>
              {!selectedAgent && (
                <div className="space-y-2">
                  <Label className="text-lux-text">Password *</Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className={`lux-input ${formErrors.password ? 'border-red-500' : ''}`}
                    placeholder="Login password for agent"
                    data-testid="agent-password-input"
                  />
                  {formErrors.password && <p className="text-red-400 text-xs">{formErrors.password}</p>}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setModalOpen(false)}
                className="lux-btn-secondary"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={saving}
                className="lux-btn-primary"
                data-testid="save-agent-btn"
              >
                {saving ? 'Saving...' : selectedAgent ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="bg-lux-card border-lux-border">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-lux-text">Delete Agent</AlertDialogTitle>
              <AlertDialogDescription className="text-lux-muted">
                Are you sure you want to delete "{selectedAgent?.full_name}"? This will also remove their user account. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="lux-btn-secondary">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
